import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { CommonIcons } from 'common-src/assets/Icons';
import { Button, RequestHandlerScreen } from 'common-src/components/base';
import { StatusItem } from 'common-src/components/elements';
import { isFeatureEnabled } from 'common-src/models/FeatureFlag';
import {
  EncounterStatus,
  encounterTypePresenter,
  getPatientEncounters,
  getPatientEncountersRequest,
  getStatusById,
  makeEncounterInactive,
} from 'common-src/models/PatientEncounter';
import {
  getAppointmentDetailsFromAthena,
  getBookedAppointmentsFromAthena,
  getPatientIntegrationsAthena,
} from 'common-src/models/PatientIntegrationsAthena';
import { Colors, ColorsNew } from 'common-src/styles';

import { ModalType } from 'src/components/base/ModalGroup';
import {
  ActionsRow,
  BaseTable,
  MultilineTextItem,
  MultiTextItem,
  TablePagination,
  TextItem,
} from 'src/components/table';
import { ResultsCount } from 'src/components/table/TablePagination/constants';
import { TabsViewNew } from 'src/components/tabs';
import { openModal } from 'src/features/modals';
import useCustomSelector from 'src/hooks/useCustomSelector';
import useInterval from 'src/hooks/useInterval';
import useRequestLoading from 'src/hooks/useRequestLoading';

import { DefaultTabInfo, EncounterTabs, TableHeaders } from './constants';
import styles from './Encounters.module.scss';
import { getTabInfo } from './helpers';

const Encounters = ({ patientId, actionsAllowed }) => {
  const dispatch = useDispatch();

  const tabs = Object.keys(EncounterTabs);
  const [selectedTab, setSelectedTab] = useState(tabs[0]);
  const [tabInfo, setTabInfo] = useState(DefaultTabInfo);
  const [filteredEncounters, setFilteredEncounters] = useState([]);
  const [paginationData, setPaginationData] = useState({ from: 0, to: undefined });
  const [athenaAppointments, setAthenaAppointments] = useState([]);
  const [isLoadingAthena, setIsLoadingAthena] = useState(false);

  const encounters = useCustomSelector((state) => getPatientEncounters(state, patientId));
  const patientIntegrationsAthena = useCustomSelector((state) =>
    getPatientIntegrationsAthena(state, patientId),
  );
  const isAthenaEnabled = useCustomSelector((state) => isFeatureEnabled(state, 'athena'));

  const { isRequesting } = useRequestLoading([getPatientEncountersRequest({ patientId })]);

  useInterval(() => {
    dispatch(getPatientEncountersRequest({ patientId, sort_order: 'desc', limit: 5 }, {}));
  });

  // Fetch Athena appointments when component mounts or when patient integration changes
  useEffect(() => {
    const fetchAthenaAppointments = () => {
      // Skip if Athena feature is not enabled
      if (!isAthenaEnabled) {
        setAthenaAppointments([]);
        return;
      }

      // Check if patient has Athena integration
      if (!patientIntegrationsAthena || patientIntegrationsAthena.length === 0) {
        setAthenaAppointments([]);
        return;
      }

      // Get the active integration (non-deleted)
      const activeIntegration = patientIntegrationsAthena.find(
        (integration) => !integration.deletedAt,
      );
      if (!activeIntegration) {
        setAthenaAppointments([]);
        return;
      }

      setIsLoadingAthena(true);

      // Fetch appointments for the past 60 days and next 30 days
      const startDate = moment().subtract(60, 'days').format('MM/DD/YYYY');
      const endDate = moment().add(30, 'days').format('MM/DD/YYYY');

      dispatch(
        getBookedAppointmentsFromAthena(
          {
            start_date: startDate,
            end_date: endDate,
            limit: 1000,
          },
          {
            successBlock: (data) => {
              // Filter appointments for this specific patient - use camelCase field name
              const patientAppointments = Array.isArray(data)
                ? data.filter((apt) => apt.athenaPatientId === activeIntegration.athenaPatientId)
                : [];

              setAthenaAppointments(patientAppointments);
              setIsLoadingAthena(false);
            },
            errorBlock: () => {
              setAthenaAppointments([]);
              setIsLoadingAthena(false);
            },
          },
        ),
      );
    };

    fetchAthenaAppointments();
  }, [patientId, patientIntegrationsAthena, isAthenaEnabled]);

  useEffect(() => {
    const { tabInfo, inProgressEncounters, completedEncounters, archivedEncounters } =
      getTabInfo(encounters);

    // Add Athena appointments count to tab info only if Athena is enabled
    const updatedTabInfo = isAthenaEnabled
      ? {
          ...tabInfo,
          ATHENA_APPOINTMENTS: {
            text: EncounterTabs.ATHENA_APPOINTMENTS.text,
            count: athenaAppointments.length,
          },
        }
      : tabInfo;
    setTabInfo(updatedTabInfo);

    let encountersToShow;
    switch (selectedTab) {
      case tabs[0]:
        encountersToShow = completedEncounters;
        break;
      case tabs[1]:
        encountersToShow = inProgressEncounters;
        break;
      case tabs[2]:
        encountersToShow = archivedEncounters;
        break;
      case tabs[3]:
        // Athena Appointments tab - we'll handle this separately in getData()
        encountersToShow = [];
        break;
      default:
        encountersToShow = [];
    }

    setFilteredEncounters(encountersToShow);
  }, [selectedTab, encounters, athenaAppointments, isAthenaEnabled]);

  const showAddEncounterPopup = () => dispatch(openModal(ModalType.ENCOUNTER, { patientId }));

  const showEncounterDetailsPopup = (encounterId) =>
    dispatch(openModal(ModalType.ENCOUNTER_DETAILS, { patientId, encounterId }));

  const showAthenaAppointmentDetails = (appointmentId) => {
    dispatch(
      getAppointmentDetailsFromAthena(appointmentId, {
        successBlock: (data) => {
          const detailsData = Array.isArray(data) ? data[0] : data;
          // Show details in a modal or alert
          alert(`Appointment Details:\n${JSON.stringify(detailsData, null, 2)}`);
        },
        errorBlock: (err) => {
          alert(`Failed to fetch appointment details: ${err}`);
        },
      }),
    );
  };

  const getData = () => {
    // If Athena Appointments tab is selected, show Athena appointments
    if (selectedTab === tabs[3]) {
      return athenaAppointments.map((appointment) => {
        const statusLabel =
          appointment.statusRaw === 'f'
            ? 'Future'
            : appointment.statusRaw === 'x'
              ? 'Cancelled'
              : appointment.statusRaw;

        // Get date and time from sourcePayload
        const appointmentDate = appointment.sourcePayload?.date || '';
        const appointmentTime = appointment.sourcePayload?.starttime || '';
        const dateTimeStr =
          appointmentDate && appointmentTime
            ? `${appointmentDate} ${appointmentTime}`
            : appointmentDate || appointmentTime || '-';

        return {
          type: (
            <MultiTextItem
              isActive
              title={appointment.appointmentType || 'Appointment'}
              subtitle={`Provider ID: ${appointment.providerAthenaId || 'N/A'}`}
              onClick={() => showAthenaAppointmentDetails(appointment.athenaAppointmentId)}
            />
          ),
          comments: (
            <MultilineTextItem
              isActive
              text={
                appointment.reason || appointment.sourcePayload?.appointmentnotes?.[0]?.text || '-'
              }
              maxLines={2}
            />
          ),
          startDate: <TextItem isActive text={dateTimeStr} value={dateTimeStr} />,
          status: (
            <StatusItem
              isActive
              withArrow={false}
              text={statusLabel}
              textColor={Colors.white}
              backgroundColor={
                appointment.statusRaw === 'f'
                  ? ColorsNew.darkGreen
                  : appointment.statusRaw === 'x'
                    ? ColorsNew.red
                    : ColorsNew.gray
              }
            />
          ),
          actions: (
            <ActionsRow
              options={[
                {
                  id: 'view-details-button',
                  text: 'View Details',
                  backgroundColor: '#555671B2',
                  color: 'white',
                  onClick: () => showAthenaAppointmentDetails(appointment.athenaAppointmentId),
                },
              ]}
            />
          ),
        };
      });
    }

    // Otherwise, show regular encounters
    return filteredEncounters.map((encounter) => {
      const isDisabled = encounter.status === EncounterStatus.Archived.value || !actionsAllowed;
      const { id, type, provider, comments, startDate, status } = encounter;
      const currentStatus = getStatusById(status);

      return {
        type: (
          <MultiTextItem
            isActive
            title={encounterTypePresenter(type)}
            subtitle={provider}
            onClick={() => showEncounterDetailsPopup(id)}
          />
        ),
        comments: <MultilineTextItem isActive text={comments || '-'} maxLines={2} />,
        startDate: (
          <TextItem
            isActive
            text={startDate ? moment(startDate).format('MM/DD/YYYY') : '-'}
            value={startDate}
          />
        ),
        status: (
          <StatusItem
            isActive
            withArrow={false}
            text={currentStatus?.label || '-'}
            textColor={currentStatus?.textColor}
            backgroundColor={currentStatus?.backgroundColor}
          />
        ),
        actions: (
          <ActionsRow
            options={[
              {
                id: 'edit-button',
                text: 'Edit',
                backgroundColor: '#555671B2',
                color: 'white',
                disabled: isDisabled,
                onClick: openModal(ModalType.ENCOUNTER, { patientId, encounterId: encounter.id }),
              },
              {
                id: 'remove-button',
                text: 'Remove',
                backgroundColor: '#F01F51B2',
                color: 'white',
                disabled: isDisabled,
                onClick: makeEncounterInactive(encounter.id, {}),
                showWarning: true,
              },
            ]}
          />
        ),
      };
    });
  };

  if (isRequesting || isLoadingAthena) {
    return <RequestHandlerScreen isRequesting requestingText="Loading..." />;
  }

  const isAthenaTab = selectedTab === tabs[3];
  const dataForPagination = isAthenaTab ? athenaAppointments : filteredEncounters;
  const emptyText = isAthenaTab ? 'No Athena appointments found' : 'No encounters';

  return (
    <>
      <TabsViewNew
        classNames={['medical-tabs']}
        options={tabInfo}
        onTabSelected={setSelectedTab}
        rightContent={
          !isAthenaTab && (
            <Button
              id="new-encounter-button"
              text="Add Encounter"
              onClick={() => showAddEncounterPopup()}
              backgroundColor={Colors.white}
              textColor={ColorsNew.darkGreen}
              iconSrc={CommonIcons.calendar}
              disabled={!actionsAllowed}
              classNames={[styles.button]}
            />
          )
        }
      />
      <BaseTable
        headers={TableHeaders}
        data={getData()}
        name={isAthenaTab ? 'athena-appointment' : 'encounter'}
        classNames={[
          'medical-table-new',
          dataForPagination?.length <= ResultsCount[25] ? 'no-pagination' : '',
        ]}
        emptyText={emptyText}
        emptyClassNames={['medical-table-empty']}
        initialSortColumn={{
          column: 'startDate',
          label: 'Start Date',
          width: '11%',
          sortBy: 'value',
        }}
        paginationData={paginationData}
      />
      <TablePagination
        onChangePageHandler={setPaginationData}
        results={dataForPagination}
        selectedTab={selectedTab}
      />
    </>
  );
};

Encounters.propTypes = {
  patientId: PropTypes.number,
  actionsAllowed: PropTypes.bool,
};

export default Encounters;
